/**
 * Event block: fires when the active state changes on a SceneObject's Properties behavior.
 * Subscribes to Properties.onStateChangedObservable.
 *
 * Flow outputs: out (fires on any state change; the one the event base class already registers)
 * Data outputs: prevState (string), newState (string)
 */

import { FlowGraphEventBlock } from "@babylonjs/core/FlowGraph/flowGraphEventBlock";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeString } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { resolveTarget } from './traceContext';
import { Properties } from '../../properties';

export class TraceOnStateChangeBlock extends FlowGraphEventBlock {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataOutput("prevState", RichTypeString);
        this.registerDataOutput("newState", RichTypeString);
    }

    public override _preparePendingTasks(context: FlowGraphContext): void {
        // Guard against re-entrancy — don't leak observers on duplicate calls
        if (context._getExecutionVariable(this, "_observer", null)) return;

        const target = resolveTarget(context, this._targetPath);
        if (!target) return;

        const props = target.getBehavior(Properties);
        if (!props) return;

        let cancelled = false;

        const observer = props.onStateChangedObservable.add(({ prev, next }) => {
            if (cancelled) return;
            this.getDataOutput("prevState")!.setValue(prev ?? '', context);
            this.getDataOutput("newState")!.setValue(next ?? '', context);
            this.out._activateSignal(context);
        });

        context._setExecutionVariable(this, "_observer", observer);
        context._setExecutionVariable(this, "_props", props);
        context._setExecutionVariable(this, "_cancel", { cancel: () => { cancelled = true; } });
    }

    public override _cancelPendingTasks(context: FlowGraphContext): void {
        const cancelHandle = context._getExecutionVariable(this, "_cancel", null) as { cancel: () => void } | null;
        cancelHandle?.cancel();
        const props = context._getExecutionVariable(this, "_props", null) as Properties | null;
        const observer = context._getExecutionVariable(this, "_observer", null) as Observer<unknown> | null;
        if (props && observer) {
            props.onStateChangedObservable.remove(observer);
        }
        // The prepare guard reads `_observer`: a start cancels first, so a stale entry left it listening to nothing.
        context._deleteExecutionVariable(this, "_observer");
        context._deleteExecutionVariable(this, "_props");
        context._deleteExecutionVariable(this, "_cancel");
    }

    public override _executeEvent(_context: FlowGraphContext, _payload: unknown): boolean {
        return true;
    }

    public override getClassName(): string {
        return "TraceOnStateChangeBlock";
    }
}
